<template lang="pug">
section.dog-creator(@click.stop :class="{standalone: !inline}")
    header
        span {{ dogDetails.name }}'s
        span  Vaccination Records
    .vaccine-table
        template(v-for="prop, key in Dogspaces.Schema.shape.vaccines.unwrap().shape")
            .full-row.big {{ prop.description }}
            template(v-if="dogDetails.vaccines?.[key]" v-for="vax in [dogDetails.vaccines[key]!]")
                .red.big(v-if="vaxExpired(vax)") Expired
                .green.big(v-else) Valid
                input.expiration(v-if="vaxFiles[key]" type="date" v-model="vaxExp[key]")
                .expiration(v-else) {{ new Date(vax.expires).toLocaleDateString('default', {year: '2-digit', month: '2-digit', day: '2-digit'}) }}
                FileUploader(@file-added="handleVax($event, key)" :file-extensions="['.png', '.jpg', '.jpeg', '.pdf']")
                    span(v-if="vaxFiles[key]") {{ vaxFiles[key]?.name }}
                    span(v-else-if="dogDetails.vaccines?.[key]")
                        VaccineDownload.button(:doc-ref="dogDetails.vaccines[key]!.ref") Download document
            template(v-else)
                div No Vaccine Uploaded
                input.expiration(v-if="vaxFiles[key]" type="date" v-model="vaxExp[key]")
                .expiration(v-else) ...
                FileUploader(@file-added="handleVax($event, key)" :file-extensions="['.png', '.jpg', '.jpeg', '.pdf']")
                    span(v-if="vaxFiles[key]") {{ vaxFiles[key]?.name }}
</template>

<script setup lang="ts">
import { useAuth } from '@/store/auth';
import { useDogspace } from '@/store/dogspaces';
import { Documents, Dogspaces } from '@busy-human/k9-library';
import { computed, reactive, ref } from 'vue';
import { ZodFirstPartyTypeKind, ZodString, ZodNumber, z } from "zod";
import VaccineDownload from './vaccine-download.vue';


type Props = {
    editDog?: string
    inline?: boolean
}
type Emits = {
    (e:'close'):void
}
const props = defineProps<Props>();


const auth = useAuth();
const dogStore = useDogspace();
const dogDetails = reactive<Dogspaces.Model>(props.editDog ? { ...dogStore.dogs[props.editDog] } : { ...Dogspaces.Defaults(), owner: auth.currentUid! })
const image = ref<File | null>(null);
const vaxFiles = ref<{[T in Documents.VAXDOCTYPE]?: File}>({});
const vaxExp = reactive<{[T in Documents.VAXDOCTYPE]?: string}>({});

const emit = defineEmits<{ (e: "close", dogId?: string): void }>();

const dogValidation = computed(() => Dogspaces.Schema.safeParse(dogDetails));

const loading = ref(false);

function handleVax(list: FileList, vax: Documents.VAXDOCTYPE) {
    const tmp = list.item(0)
    if(tmp)
        vaxFiles.value[vax] = tmp;
    // console.log(tmp);
}

function vaxExpired(vax: z.infer<typeof Dogspaces.VaccineRefSchema>) {
    if(!vax) return false;
    return new Date(vax.expires).getTime() < new Date().getTime();
}

const vaccinesReady = computed(() => {
    for(const key in vaxFiles.value) {
        if(!vaxExp[key as Documents.VAXDOCTYPE]) return false;
    }
    return true;
})
</script>

<style lang="scss">
section.dog-creator {
    width: 100%;
    height: 100%;
    padding: 2rem;
    box-sizing: border-box;
    position: relative;

    * {
        box-sizing: border-box;
    }

    &.standalone {
        background: white;
        border-radius: .25rem;
        border: 1px solid black;
        width: unset;
        height: unset;
    }

    header {
        font-size: x-large;
        margin-bottom: 2rem;
    }

    .vaccine-table {
        display: grid;
        grid-template-columns: repeat(3, auto);
        gap: 1rem;

        a {
            text-decoration: none;
        }

        .full-row {
            margin: 1rem 0 .25rem;
            grid-column: 1 / -1;
        }

        & > :not(.full-row) {
            align-self: center;
        }

        .big {
            font-size: 1.5em;
        }

        .green {
            color: #00bbae;
        }
        .red {
            color: $color-red;
        }
        .expiration {
            justify-self: center;
        }
    }

    aside.loader-overlay {
        position: absolute;
        width: 100%; height: 100%;
        left: 0; top: 0;

        background: rgba(white, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2em;
    }
}
</style>